import _asyncToGenerator from "/app/node_modules/.pnpm/@babel+runtime@7.26.0/node_modules/@babel/runtime/helpers/esm/asyncToGenerator.js";
import _slicedToArray from "/app/node_modules/.pnpm/@babel+runtime@7.26.0/node_modules/@babel/runtime/helpers/esm/slicedToArray.js";
import _regeneratorRuntime from "/app/node_modules/.pnpm/@babel+runtime@7.26.0/node_modules/@babel/runtime/regenerator/index.js";
import React, { useState, useRef, useCallback } from 'react';
import useSWR, { useSWRConfig } from 'swr';
import { IS_REACT_LEGACY, useIsomorphicLayoutEffect, withMiddleware, serialize, mergeObjects, getTimestamp, UNDEFINED } from 'swr/_internal';
var startTransition = IS_REACT_LEGACY ? function (cb) {
  cb();
} : React.startTransition;
/**
 * An implementation of state with dependency-tracking.
 */
var useStateWithDeps = function useStateWithDeps(state) {
  var _useState = useState({}),
    _useState2 = _slicedToArray(_useState, 2),
    rerender = _useState2[1];
  var unmountedRef = useRef(false);
  var stateRef = useRef(state);
  // If a state property (data, error, or isValidating) is accessed by the render
  // function, we mark the property as a dependency so if it is updated again
  // in the future, we trigger a rerender.
  // This is also known as dependency-tracking.
  var stateDependenciesRef = useRef({
    data: false,
    error: false,
    isValidating: false
  });
  /**
  * @param payload To change stateRef, pass the values explicitly to setState:
  * @example
  * ```js
  * setState({
  *   isValidating: false
  *   data: newData // set data to newData
  *   error: undefined // set error to undefined
  * })
  *
  * setState({
  *   isValidating: false
  *   data: undefined // set data to undefined
  *   error: err // set error to err
  * })
  * ```
  */
  var setState = useCallback(function (payload) {
    var shouldRerender = false;
    var currentState = stateRef.current;
    for (var _ in payload) {
      var k = _;
      // If the property has changed, update the state and mark rerender as
      // needed.
      if (currentState[k] !== payload[k]) {
        currentState[k] = payload[k];
        // If the property is accessed by the component, a rerender should be
        // triggered.
        if (stateDependenciesRef.current[k]) {
          shouldRerender = true;
        }
      }
    }
    if (shouldRerender && !unmountedRef.current) {
      rerender({});
    }
  }, []);
  useIsomorphicLayoutEffect(function () {
    unmountedRef.current = false;
    return function () {
      unmountedRef.current = true;
    };
  });
  return [stateRef, stateDependenciesRef.current, setState];
};
var mutation = function mutation() {
  return function (key, fetcher) {
    var config = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
    var _useSWRConfig = useSWRConfig(),
      mutate = _useSWRConfig.mutate;
    var keyRef = useRef(key);
    var fetcherRef = useRef(fetcher);
    var configRef = useRef(config);
    // Ditch all mutation results that happened earlier than this timestamp.
    var ditchMutationsUntilRef = useRef(0);
    var _useStateWithDeps = useStateWithDeps({
        data: UNDEFINED,
        error: UNDEFINED,
        isMutating: false
      }),
      _useStateWithDeps2 = _slicedToArray(_useStateWithDeps, 3),
      stateRef = _useStateWithDeps2[0],
      stateDependencies = _useStateWithDeps2[1],
      setState = _useStateWithDeps2[2];
    var currentState = stateRef.current;
    var trigger = useCallback(/*#__PURE__*/function () {
      var _ref = _asyncToGenerator(/*#__PURE__*/_regeneratorRuntime.mark(function _callee(arg, opts) {
        var _serialize, _serialize2, serializedKey, resolvedKey, options, mutationStartedAt, data, _options_onSuccess, _options, _options_onError, _options1;
        return _regeneratorRuntime.wrap(function _callee$(_context) {
          while (1) switch (_context.prev = _context.next) {
            case 0:
              _serialize = serialize(keyRef.current), _serialize2 = _slicedToArray(_serialize, 2), serializedKey = _serialize2[0], resolvedKey = _serialize2[1];
              if (fetcherRef.current) {
                _context.next = 3;
                break;
              }
              throw new Error('Can’t trigger the mutation: missing fetcher.');
            case 3:
              if (serializedKey) {
                _context.next = 5;
                break;
              }
              throw new Error('Can’t trigger the mutation: missing key.');
            case 5:
              // Disable cache population by default.
              options = mergeObjects(mergeObjects({
                populateCache: false,
                throwOnError: true
              }, configRef.current), opts); // Trigger a mutation, and also track the timestamp. Any mutation that happened
              // earlier this timestamp should be ignored.
              mutationStartedAt = getTimestamp();
              ditchMutationsUntilRef.current = mutationStartedAt;
              setState({
                isMutating: true
              });
              _context.prev = 9;
              _context.next = 12;
              return mutate(serializedKey, fetcherRef.current(resolvedKey, {
                arg: arg
              }),
              // We must throw the error here so we can catch and update the states.
              mergeObjects(options, {
                throwOnError: true
              }));
            case 12:
              data = _context.sent;
              // If it's reset after the mutation, we don't broadcast any state change.
              if (ditchMutationsUntilRef.current <= mutationStartedAt) {
                startTransition(function () {
                  return setState({
                    data: data,
                    isMutating: false,
                    error: undefined
                  });
                });
                (_options_onSuccess = (_options = options).onSuccess) == null ? void 0 : _options_onSuccess.call(_options, data, serializedKey, options);
              }
              return _context.abrupt("return", data);
            case 17:
              _context.prev = 17;
              _context.t0 = _context["catch"](9);
              if (!(ditchMutationsUntilRef.current <= mutationStartedAt)) {
                _context.next = 24;
                break;
              }
              startTransition(function () {
                return setState({
                  error: _context.t0,
                  isMutating: false
                });
              });
              (_options_onError = (_options1 = options).onError) == null ? void 0 : _options_onError.call(_options1, _context.t0, serializedKey, options);
              if (!options.throwOnError) {
                _context.next = 24;
                break;
              }
              throw _context.t0;
            case 24:
            case "end":
              return _context.stop();
          }
        }, _callee, null, [[9, 17]]);
      }));
      return function (_x, _x2) {
        return _ref.apply(this, arguments);
      };
    }(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []);
    var reset = useCallback(function () {
      ditchMutationsUntilRef.current = getTimestamp();
      setState({
        data: UNDEFINED,
        error: UNDEFINED,
        isMutating: false
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    useIsomorphicLayoutEffect(function () {
      keyRef.current = key;
      fetcherRef.current = fetcher;
      configRef.current = config;
    });
    // We don't return `mutate` here as it can be pretty confusing (e.g. people
    // calling `mutate` but they actually mean `trigger`).
    // And also, `mutate` relies on the useSWR hook to exist too.
    return {
      trigger: trigger,
      reset: reset,
      get data() {
        stateDependencies.data = true;
        return currentState.data;
      },
      get error() {
        stateDependencies.error = true;
        return currentState.error;
      },
      get isMutating() {
        stateDependencies.isMutating = true;
        return currentState.isMutating;
      }
    };
  };
};
/**
 * A hook to define and manually trigger remote mutations like POST, PUT, DELETE and PATCH use cases.
 *
 * @link https://swr.vercel.app/docs/mutation
 * @example
 * ```jsx
 * import useSWRMutation from 'swr/mutation'
 *
 * const {
 *   data,
 *   error,
 *   trigger,
 *   reset,
 *   isMutating
 * } = useSWRMutation(key, fetcher, options?)
 * ```
 */
var useSWRMutation = withMiddleware(useSWR, mutation);
export { useSWRMutation as default };